import {
  Box,
  Checkbox,
  MenuItem,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import DeleteIcon from "src/assets/icon/delete";
import EditIcon from "src/assets/icon/edit";
import Label from "src/components/Label";
import { CatalogCustomProperties } from "src/components/shared/catalog/CatalogCustomProperties";
import { TableMoreMenu } from "src/components/table";
import usePermissions from "src/hooks/usePermissions";
import { ITax } from "../types";

interface ITaxesTableRowProps {
  row: ITax;
  selected: boolean;
  onSelectRow: VoidFunction;
  fetchData: (showLoader?: boolean) => Promise<void>;
  handleOpenEditModal: VoidFunction;
  handleToggleDeleteModal: VoidFunction;
  translate: (key: string) => string;
}

export const TaxesTableRow: React.FC<ITaxesTableRowProps> = ({
  row,
  selected,
  onSelectRow,
  fetchData,
  handleOpenEditModal,
  handleToggleDeleteModal,
  translate,
}) => {
  const theme = useTheme();
  const { permissionHandle } = usePermissions();
  const [openMenu, setOpenMenuActions] = useState<null | HTMLElement>(null);
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) =>
    setOpenMenuActions(event.currentTarget);
  const handleCloseMenu = () => setOpenMenuActions(null);
  const isInactive = row.isActive === false;

  const [tooltipEnabled, setTooltipEnabled] = useState(false);

  const handleShowTooltip = ({ currentTarget }: React.MouseEvent<Element>) => {
    const isOverflowing =
      currentTarget.scrollWidth > currentTarget.clientWidth ||
      currentTarget.scrollHeight > currentTarget.clientHeight;
    setTooltipEnabled(isOverflowing);
  };

  const hideTooltip = () => setTooltipEnabled(false);

  return (
    <TableRow hover selected={selected}>
      <TableCell padding="checkbox">
        <Checkbox
          checked={selected}
          onClick={onSelectRow}
          size="small"
          disabled={isInactive}
        />
      </TableCell>

      <TableCell align="left" sx={{ width: "50%" }}>
        <Box display="flex" flexDirection="column">
          <Typography variant="body2">{row.code}</Typography>
          <Box sx={{ mt: 0.5 }}>
            <Label
              color={row.isActive ? "success" : "error"}
              sx={{ textTransform: "capitalize" }}
            >
              {row.isActive ? translate("active") : translate("inactive")}
            </Label>
          </Box>
          <CatalogCustomProperties customProperties={row.customProperties} />
        </Box>
      </TableCell>

      <TableCell align="left">
        {row?.description && (
          <Tooltip
            title={row.description}
            onMouseEnter={handleShowTooltip}
            onMouseLeave={hideTooltip}
            disableHoverListener={!tooltipEnabled}
            placement="top"
          >
            <Typography
              color={theme.palette.text.primary}
              sx={{
                fontSize: "12px",
                width: 230,
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
              }}
            >
              {row.description}
            </Typography>
          </Tooltip>
        )}
      </TableCell>

      <TableCell align="left">
        <Typography variant="body2">{row.taxType}</Typography>
      </TableCell>

      <TableCell align="left">
        <Typography variant="body2">{row.rate}%</Typography>
      </TableCell>

      <TableCell align="left">
        <Label
          variant={theme.palette.mode === "light" ? "ghost" : "filled"}
          color={row.isWithholdingTax ? "success" : "error"}
          sx={{ textTransform: "capitalize" }}
        >
          {row.isWithholdingTax ? translate("yes") : translate("no")}
        </Label>
      </TableCell>
      <TableCell align="left">
        <TableMoreMenu
          open={openMenu}
          onOpen={handleOpenMenu}
          onClose={handleCloseMenu}
          disabled={isInactive}
          actions={
            <>
              <MenuItem
                disabled={!permissionHandle(["Taxes_Write"])}
                onClick={() => {
                  handleOpenEditModal();
                  handleCloseMenu();
                }}
              >
                <EditIcon
                  height={20}
                  width={20}
                  sx={{
                    height: "auto",
                    width: "auto",
                  }}
                  customColor={theme.palette?.action.active}
                />
                {translate("edit")}
              </MenuItem>

              <MenuItem
                disabled={!permissionHandle(["Taxes_Write"])}
                onClick={() => {
                  handleToggleDeleteModal();
                  handleCloseMenu();
                }}
              >
                <DeleteIcon
                  height={20}
                  width={20}
                  sx={{
                    height: "auto",
                    width: "auto",
                  }}
                  customColor={theme.palette?.action.active}
                />
                {translate("delete")}
              </MenuItem>
            </>
          }
        />
      </TableCell>
    </TableRow>
  );
};
